<template>
  <v-container fluid>
    <div class="d-flex align-center flex-wrap mb-4 actuarial__header">
      <h2 class="text-h5 mr-3">Análisis actuarial</h2>
      <v-spacer />
      <v-select v-model="countryIso" :items="countries" label="País" dense outlined hide-details class="actuarial__filter" />
      <v-select v-model="snapshot" :items="snapshotOptions" label="Foto" dense outlined hide-details class="actuarial__filter" />
      <v-btn v-if="canPublish" text color="primary" :loading="computing" @click="compute">Recalcular ahora</v-btn>
    </div>

    <v-alert v-if="error" type="warning" text dense>{{ error }}</v-alert>
    <p v-if="margins" class="text-body-2 mb-4">
      Reembolsos aprobados hasta el {{ margins.periodTo }}, 12 meses. Costo con prudencia de 1,3.
      <span v-if="margins.incompleteMonth">El último mes todavía tiene reembolsos en revisión.</span>
    </p>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab>Márgenes de planes</v-tab>
      <v-tab>Partners</v-tab>
      <v-tab>Uso por servicio</v-tab>
      <v-tab>Fotos</v-tab>
    </v-tabs>
    <v-progress-linear v-if="loading" indeterminate class="mb-3" />

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <template v-if="margins">
          <v-select
            v-model="marginsLine"
            :items="marginLineOptions"
            label="Línea"
            outlined
            dense
            hide-details
            class="actuarial__line mb-4"
          />
          <actuarial-plan-margins :plans="marginView.plans" :currency="margins.currency" v-bind="marginView.labels" />
        </template>
      </v-tab-item>
      <v-tab-item>
        <actuarial-partner-margins v-if="partners" :offers="partners.offers" :currency="margins ? margins.currency : 'CLP'" />
      </v-tab-item>
      <v-tab-item>
        <actuarial-services v-if="servicesModel" :model="servicesModel" :currency="servicesModel.currency" />
      </v-tab-item>
      <v-tab-item>
        <v-simple-table dense class="actuarial__table">
          <thead>
            <tr>
              <th>Calculada</th>
              <th>Datos hasta</th>
              <th>Cierre de mes</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in snapshots" :key="row.id">
              <td>{{ row.computedAt | moment('YYYY-MM-DD HH:mm') }}</td>
              <td>{{ row.periodTo }}</td>
              <td>{{ row.isMonthClose ? 'Sí' : '' }}</td>
              <td>{{ statusLabel(row.status) }}</td>
              <td class="text-right">
                <v-btn
                  v-if="canPublish && row.status === 'computed'"
                  x-small
                  text
                  color="primary"
                  @click="openPublish(row)"
                >
                  Revisar y publicar
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="publishOpen" max-width="720" scrollable>
      <v-card>
        <v-card-title class="text-h6">Publicar foto para el cotizador</v-card-title>
        <v-card-text>
          <p class="text-body-2">
            Costo por membresía de los planes estándar ({{ countryIso }}) con esta foto, contra la que usa hoy el cotizador.
            Las cotizaciones ya guardadas no cambian.
          </p>
          <v-progress-linear v-if="diffLoading" indeterminate class="mb-2" />
          <v-simple-table v-else dense class="actuarial__table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Colaboradores</th>
                <th class="text-right">Hoy</th>
                <th class="text-right">Con esta foto</th>
                <th class="text-right">Cambio</th>
                <th class="text-right">Margen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in diffRows" :key="`${row.plan}-${row.from}`">
                <td>{{ planName(row.plan) }}</td>
                <td>{{ range(row.from, row.to) }}</td>
                <td class="text-right">{{ row.costBefore === null ? '—' : money(row.costBefore) }}</td>
                <td class="text-right">{{ money(row.costAfter) }}</td>
                <td class="text-right">{{ row.change === null ? 'Primera foto' : signedPct(row.change) }}</td>
                <td class="text-right">{{ pct(row.marginAfter) }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <template v-if="!diffLoading">
            <h4 class="text-subtitle-2 font-weight-bold mt-5 mb-1">Servicios cuyo uso cambió</h4>
            <p class="text-caption mb-2">
              Frecuencia al año por mascota medida en los reembolsos, con cambios de 5% o más. El motivo son los
              reembolsos y mascota-años nuevos que entraron.
            </p>
            <v-simple-table v-if="diffServices.length" dense class="actuarial__table">
              <thead>
                <tr>
                  <th>Servicio</th>
                  <th class="text-right">Hoy</th>
                  <th class="text-right">Con esta foto</th>
                  <th class="text-right">Cambio</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in diffServices" :key="`${row.line}-${row.code}`">
                  <td>{{ serviceName(row.code) }} <span class="text-caption">· {{ lineLabel(row.line) }}</span></td>
                  <td class="text-right">{{ row.frequencyBefore === null ? '—' : frequency(row.frequencyBefore) }}</td>
                  <td class="text-right">{{ frequency(row.frequencyAfter) }}</td>
                  <td class="text-right">{{ row.change === null ? 'Nuevo' : signedPct(row.change) }}</td>
                  <td class="text-caption">{{ reasonText(row) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <p v-else class="text-body-2">Ningún servicio cambió 5% o más.</p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="publishOpen = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="Boolean(publishing)" :disabled="diffLoading" @click="publish(publishRow)">
            Publicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" top>{{ snackbarMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import ActuarialPlanMargins from '../components/ActuarialPlanMargins.vue';
import ActuarialServices from '../components/ActuarialServices.vue';
import ActuarialPartnerMargins from '../components/ActuarialPartnerMargins.vue';
import ActuarialService from '../services/actuarial.service';
import { utilsMixin } from '../mixins/utilsMixin';
import { ASSISTANCE_PRODUCT_OPTIONS, COUNTRY_OPTIONS, formatMoney } from '../utils/b2b-quotes.utils';
import { LINE_LABELS, pct, rangeText, serviceName, SNAPSHOT_STATUS } from '../utils/actuarial.utils';

const MODALITY_NAMES = { capitated: 'capitado', voluntary: 'voluntario' };
/** Short product name: the option text without its examples in parentheses. */
const productName = (product) =>
  (ASSISTANCE_PRODUCT_OPTIONS.find((o) => o.value === product)?.text ?? product).replace(/\s*\(.*\)$/, '');

const ERRORS = {
  ACTUARIAL_SNAPSHOT_NOT_FOUND: 'Todavía no hay una foto calculada (o publicada). Un aprobador puede recalcular ahora.',
  PRICING_PARAMETERS_NOT_LOADED: 'Faltan los parámetros del Sheet: cárgalos desde Cotizaciones.',
  PRICING_APPROVER_REQUIRED: 'Solo un aprobador de precios puede hacer esto.'
};

/** Finanzas → Análisis actuarial (pawer-backend spec 095): margins and usage from real refunds. */
export default {
  name: 'AnalisisActuarial',
  components: { ActuarialPlanMargins, ActuarialServices, ActuarialPartnerMargins },
  mixins: [utilsMixin],
  data() {
    return {
      tab: 0,
      countryIso: 'CL',
      snapshot: 'latest',
      countries: COUNTRY_OPTIONS,
      snapshotOptions: [
        { value: 'latest', text: 'Último cálculo' },
        { value: 'published', text: 'Publicada (la del cotizador)' }
      ],
      margins: null,
      marginsLine: 'eb',
      servicesModel: null,
      partners: null,
      snapshots: [],
      canPublish: false,
      loading: false,
      computing: false,
      publishing: null,
      publishOpen: false,
      publishRow: null,
      diffRows: [],
      diffServices: [],
      diffLoading: false,
      error: '',
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    /** Employee Benefit collective, then each Asistencias product × modality the backend priced. */
    marginLineOptions() {
      const assistance = (this.margins?.assistance ?? []).map((block) => ({
        value: `${block.product}:${block.modality}`,
        text: `Asistencias · ${productName(block.product)}, ${MODALITY_NAMES[block.modality] ?? block.modality}`
      }));
      return [{ value: 'eb', text: 'Employee Benefit · colectivo' }, ...assistance];
    },
    marginView() {
      const block = (this.margins?.assistance ?? []).find((b) => `${b.product}:${b.modality}` === this.marginsLine);
      if (!block) return { plans: this.margins?.plans ?? [], labels: {} };
      return {
        plans: block.plans,
        labels: {
          planSuffix: `${productName(block.product).toLowerCase()}, ${MODALITY_NAMES[block.modality] ?? block.modality}`,
          unitLabel: 'Asegurados',
          priceLabel: 'Precio',
          petsLabel: 'Mascotas por asegurado',
          petsHint: 'Activación del producto: mascotas activas ÷ asegurados',
          costLabel: 'Costo por asegurado'
        }
      };
    }
  },
  watch: {
    countryIso() {
      this.load();
    },
    snapshot() {
      this.load();
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      const [margins, services, snapshots, partners] = await Promise.all([
        ActuarialService.planMargins(this.countryIso, this.snapshot),
        ActuarialService.services(this.countryIso, this.snapshot),
        ActuarialService.listSnapshots(),
        ActuarialService.partnerOffers(this.countryIso, this.snapshot)
      ]);
      this.partners = partners.status === 'ok' ? partners.data : null;
      this.loading = false;
      this.margins = margins.status === 'ok' ? margins.data : null;
      this.servicesModel = services.status === 'ok' ? services.data : null;
      if (snapshots.status === 'ok') {
        this.snapshots = snapshots.data;
        this.canPublish = Boolean(snapshots.meta?.canPublish);
      }
      const failed = [margins, services].find((r) => r.status !== 'ok');
      if (failed) this.error = ERRORS[failed.code] ?? 'No se pudo cargar el análisis.';
    },
    async compute() {
      this.computing = true;
      const res = await ActuarialService.compute();
      this.computing = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(ERRORS[res.code] ?? 'No se pudo recalcular.');
        return;
      }
      this.showSuccessSnackbar('Foto recalculada');
      this.load();
    },
    async openPublish(row) {
      this.publishRow = row;
      this.publishOpen = true;
      this.diffLoading = true;
      const res = await ActuarialService.diff(row.id, this.countryIso);
      this.diffLoading = false;
      this.diffRows = res.status === 'ok' ? res.data : [];
      this.diffServices = res.status === 'ok' ? res.meta?.services ?? [] : [];
      if (res.status !== 'ok') this.showErrorSnackbar(ERRORS[res.code] ?? 'No se pudo comparar.');
    },
    serviceName,
    lineLabel(line) {
      return LINE_LABELS[line] ?? line;
    },
    frequency(value) {
      return value.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    },
    /** Why a usage moved: the reimbursements and exposure added since the published snapshot. */
    reasonText(row) {
      const count = (n) => Math.round(n).toLocaleString('es-CL');
      if (row.eventsBefore === null) return `${count(row.eventsAfter)} reembolsos en ${count(row.petYearsAfter)} mascota-años`;
      const events = row.eventsAfter - row.eventsBefore;
      const years = row.petYearsAfter - row.petYearsBefore;
      const sign = (n) => (n >= 0 ? '+' : '−');
      return `${sign(events)}${count(Math.abs(events))} reembolsos, ${sign(years)}${count(Math.abs(years))} mascota-años`;
    },
    planName(code) {
      return code === 'wellness' ? 'Wellness' : 'Urgencias';
    },
    money(value) {
      return formatMoney(value, this.margins?.currency ?? 'CLP');
    },
    range: rangeText,
    pct,
    signedPct(value) {
      const sign = value > 0 ? '+' : '';
      return `${sign}${pct(value, 1)}`;
    },
    async publish(row) {
      this.publishing = row.id;
      const res = await ActuarialService.publish(row.id);
      this.publishing = null;
      this.publishOpen = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(ERRORS[res.code] ?? 'No se pudo publicar.');
        return;
      }
      this.showSuccessSnackbar('Foto publicada: el cotizador la usa desde ahora');
      this.load();
    },
    statusLabel(status) {
      return SNAPSHOT_STATUS[status] ?? status;
    }
  }
};
</script>

<style scoped>
.actuarial__line {
  max-width: 420px;
}
.actuarial__header {
  gap: 8px;
}
.actuarial__filter {
  max-width: 220px;
}
.actuarial__table {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
}
</style>
